import { useEffect, useRef, useState } from 'react';
import { eventX, eventY } from '../helpers/events';
import { useNodeRef } from './useNodeRef';
import { HOOKS_TAP_TOLERANCE } from '../constants';
import { useVariable } from './useVariable';
import { useDidUpdateEffect } from './useDidUpdateEffect';
import { useFunction } from './useFunction';
export const useSwipe = (onStart, onEnd, initialRef, useTouch = true, disabled = false, noEventsPropagation = false, noEventsDefault = false, useClickPrevention = true, useScrollOnTouch = 'auto', tolerance = HOOKS_TAP_TOLERANCE) => {
  const handleEnd = useVariable(onEnd);
  const handleStart = useVariable(onStart);
  const [offsetPosition, setOffsetPosition] = useState({
    x: 0,
    y: 0
  });
  const startX = useRef(null);
  const startY = useRef(null);
  const lastX = useRef(null);
  const lastY = useRef(null);
  const started = useRef(false);
  const moved = useRef(false);
  const direction = useRef(null);
  const click = useFunction(e => {
    if (disabled) {
      return;
    }
    if (useClickPrevention && moved.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  });
  const end = useFunction(e => {
    if (disabled) {
      return;
    }
    if (noEventsPropagation) {
      e.stopPropagation();
    }
    if (!started.current) {
      return;
    }
    const currentX = eventX(e, e.changedTouches);
    const currentY = eventY(e, e.changedTouches);
    if (!useTouch || useScrollOnTouch === false || direction.current === 'horizontal' && useScrollOnTouch === 'auto') {
      handleEnd.current?.(e, {
        x: startX.current - (currentX !== null ? currentX : lastX.current),
        y: startY.current - (currentY !== null ? currentY : lastY.current)
      }, {
        x: startX.current,
        y: startY.current
      });
    }
    cancel();
  });
  const move = useFunction(e => {
    if (disabled) {
      return;
    }
    if (noEventsPropagation) {
      e.stopPropagation();
    }
    if (!started.current) {
      return;
    }
    lastX.current = eventX(e);
    lastY.current = eventY(e);
    const x = startX.current - lastX.current;
    const y = startY.current - lastY.current;
    if (!direction.current) {
      direction.current = Math.abs(x) >= Math.abs(y) ? 'horizontal' : 'vertical';
    }
    if (!moved.current && (Math.abs(x) > tolerance || Math.abs(y) > tolerance)) {
      moved.current = true;
    }
    if (!useTouch || useScrollOnTouch === false || direction.current === 'horizontal' && useScrollOnTouch === 'auto') {
      setOffsetPosition({
        x,
        y
      });
    }
    if (useTouch && !(useScrollOnTouch === true || direction.current === 'vertical' && useScrollOnTouch === 'auto')) {
      e.preventDefault();
      return false;
    }
  });
  const unbind = () => {
    window.removeEventListener('touchcancel', cancel);
    window.removeEventListener('touchend', end);
    window.removeEventListener('mouseup', end);
    window.removeEventListener('mousemove', move);
  };
  const bind = () => {
    window.addEventListener('touchcancel', cancel);
    window.addEventListener('touchend', end);
    window.addEventListener('mouseup', end);
    window.addEventListener('mousemove', move);
  };
  const cancel = useFunction(() => {
    started.current = false;
    direction.current = null;
    setOffsetPosition({
      x: 0,
      y: 0
    });
    unbind();
  });
  const start = useFunction(e => {
    if (disabled) {
      return;
    }
    if (noEventsDefault) {
      e.preventDefault();
    }
    if (noEventsPropagation) {
      e.stopPropagation();
    }
    started.current = true;
    moved.current = false;
    startX.current = eventX(e);
    startY.current = eventY(e);
    lastX.current = null;
    lastY.current = null;
    setOffsetPosition({
      x: 0,
      y: 0
    });
    handleStart.current?.(e, {
      x: startX.current,
      y: startY.current
    });
    direction.current = null;
    bind();
  });
  useDidUpdateEffect(() => {
    if (disabled) {
      started.current = false;
      setOffsetPosition({
        x: 0,
        y: 0
      });
    }
  }, [disabled]);
  useEffect(() => unbind, []);
  const [setRef, nodeRef] = useNodeRef(initialRef, node => {
    node.addEventListener('click', click, {
      passive: !useClickPrevention
    });
    node.addEventListener('touchstart', start, {
      passive: !noEventsPropagation && useScrollOnTouch === true
    });
    node.addEventListener('touchmove', move, {
      passive: !noEventsPropagation && useScrollOnTouch === true
    });
    node.addEventListener('mousedown', start);
  });
  return {
    nodeRef,
    setRef,
    offsetPosition
  };
};